import React, { useEffect, useState } from "react";
import { api } from "../utils/api";
import { useI18n } from "../context/I18nContext";
import { useApp } from "../context/AppContext";

export default function Dietitian() {
  const { t, language } = useI18n();
  const { state, actions } = useApp();
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [limitInfo, setLimitInfo] = useState(null);
  const hasMedicalProfile = Boolean(state.user?.pastIllnesses || state.user?.chronicDiseases || state.user?.allergies || state.user?.medications);
  const hasBodyProfile = Boolean(state.user?.healthMetrics?.bmi);

  useEffect(() => {
    setLoading(true);
    api.getDietitianAdvice(language, actions.usageProfile())
      .then((response) => setAdvice(response.data))
      .catch(() => setAdvice(null))
      .finally(() => setLoading(false));
  }, [language, state.user?.id, state.user?.profileUpdatedAt]);

  const handleAsk = async () => {
    if (!question.trim() || asking) return;
    const currentQuestion = question.trim();
    setQuestion("");
    await submitQuestion(currentQuestion, true);
  };

  const submitQuestion = async (currentQuestion, appendUserMessage = false) => {
    setAsking(true);
    setLimitInfo(null);
    if (appendUserMessage) {
      setChatHistory((items) => [...items, { role: "user", text: currentQuestion }]);
    }
    try {
      const response = await api.askDietitian(currentQuestion, language, actions.usageProfile());
      setChatHistory((items) => [...items, { role: "ai", text: response.answer }]);
    } catch (error) {
      if (error?.code === "LIMIT_REACHED") {
        setLimitInfo(error.usage || {});
      } else {
        setChatHistory((items) => [...items, { role: "ai", text: t("cannotAnswerNow") }]);
      }
    } finally {
      setAsking(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>🥗 {t("dietitian")}</h1>
        <p>{t("dietitianIntro")}</p>
      </div>

      {hasBodyProfile || hasMedicalProfile ? (
        <div className="profile-coach-note">
          <strong>{t("profileLinkedToDietitian")}</strong>
          <span>
            {hasBodyProfile ? `${t("bmi")}: ${state.user.healthMetrics.bmi} · ${t("idealWeightRange")}: ${state.user.healthMetrics.idealMinKg}-${state.user.healthMetrics.idealMaxKg} kg` : t("medicalProfileLinked")}
            {hasBodyProfile && hasMedicalProfile ? ` · ${t("medicalProfileLinked")}` : ""}
          </span>
        </div>
      ) : state.user ? (
        <div className="profile-coach-note muted">
          <strong>{t("profileNotCompleted")}</strong>
          <span>{t("profileNotCompletedHint")}</span>
        </div>
      ) : null}

      {loading ? (
        <div className="loading"><div className="spinner" /><p>{t("adviceLoading")}</p></div>
      ) : advice ? (
        <div className="dietitian-card">
          <div className="dietitian-header">
            <div className="dietitian-avatar">🥗</div>
            <div>
              <div className="dietitian-name">{t("dietitian")}</div>
              <div className="dietitian-title">{t("dailyNutritionAdvice")}</div>
              <div className="dietitian-date">
                {new Date().toLocaleDateString(language === "tr" ? "tr-TR" : undefined, { weekday: "long", day: "numeric", month: "long" })}
              </div>
            </div>
            <div className="dietitian-topic-badge">
              <span>{advice.emoji}</span>
              <span>{advice.topic}</span>
            </div>
          </div>

          <div className="dietitian-body">
            <div className="dietitian-greeting">{advice.greeting}</div>
            <div className="dietitian-main">{advice.mainAdvice}</div>

            <div className="dietitian-tips">
              <div className="tips-title">{t("dailyTips")}</div>
              {advice.tips?.map((tip, index) => (
                <div key={index} className="tip-item">
                  <span className="tip-num">{index + 1}</span>
                  <span>{tip}</span>
                </div>
              ))}
            </div>

            {advice.foodSuggestion && (
              <div className="dietitian-food">
                <span className="food-label">{t("suggestedFoodToday")}</span>
                <span>{advice.foodSuggestion}</span>
              </div>
            )}

            <div className="dietitian-motivation">{advice.motivation}</div>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">🥗</div>
          <h3>{t("adviceLoadFailed")}</h3>
          <p>{t("checkBackendOrKey")}</p>
        </div>
      )}

      <div className="dietitian-chat">
        <div className="chat-header">
          <span>💬</span>
          <h2>{t("askDietitian")}</h2>
        </div>
        <p className="chat-note">{t("medicalDisclaimer")}</p>
        {limitInfo && (
          <div className="reward-box">
            <strong>Bugünkü soru limitine ulaştın.</strong>
            <span>Kullanım hakkın Europe/Istanbul saatine göre gece 00.00'da yenilenir.</span>
          </div>
        )}

        {chatHistory.length > 0 && (
          <div className="chat-history">
            {chatHistory.map((message, index) => (
              <div key={index} className={`chat-msg ${message.role}`}>
                <div className="chat-bubble">
                  <div className="chat-text">{message.text}</div>
                </div>
              </div>
            ))}
            {asking && <div className="chat-msg ai"><div className="chat-bubble"><div className="chat-text">{t("answerPreparing")}</div></div></div>}
          </div>
        )}

        <div className="chat-input-row">
          <input
            type="text"
            placeholder={t("askPlaceholder")}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && handleAsk()}
            disabled={asking}
          />
          <button className="btn btn-primary" onClick={handleAsk} disabled={!question.trim() || asking}>
            {asking ? t("wait") : t("ask")}
          </button>
        </div>
      </div>
    </div>
  );
}
